import { useMemo } from 'react'

import {
  F0Form,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
  type F0FormSubmitResult,
} from '@factorialco/f0-react'
import { z } from 'zod'

import {
  AtsJobRequirementsOptionsEnum,
  UpdateOpenApplicationSettingDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

const REQUIREMENT_VALUES = ['mandatory', 'optional', 'do_not_ask'] as const

const buildSchema = () => {
  const requirementOptions: Array<{ label: string; value: AtsJobRequirementsOptionsEnum }> = [
    { label: i18n.t('ats.jobs_form.mandatory'), value: 'mandatory' },
    { label: i18n.t('ats.jobs_form.optional'), value: 'optional' },
    { label: i18n.t('ats.jobs_form.do_not_ask'), value: 'do_not_ask' },
  ]

  return z.object({
    cvRequirement: f0FormField(z.enum(REQUIREMENT_VALUES), {
      label: i18n.t('ats.settings.candidate_data.open_application_settings.form.cv'),
      fieldType: 'select',
      options: requirementOptions,
      row: 'identification',
    }),
    phoneRequirement: f0FormField(z.enum(REQUIREMENT_VALUES), {
      label: i18n.t('ats.settings.candidate_data.open_application_settings.form.phone'),
      fieldType: 'select',
      options: requirementOptions,
      row: 'identification',
    }),
    photoRequirement: f0FormField(z.enum(REQUIREMENT_VALUES), {
      label: i18n.t('ats.settings.candidate_data.open_application_settings.form.photo'),
      fieldType: 'select',
      options: requirementOptions,
      row: 'profile',
    }),
    personalUrlRequirement: f0FormField(z.enum(REQUIREMENT_VALUES), {
      label: i18n.t('ats.settings.candidate_data.open_application_settings.form.personalUrl'),
      fieldType: 'select',
      options: requirementOptions,
      row: 'profile',
    }),
    genderRequirement: f0FormField(z.enum(REQUIREMENT_VALUES), {
      label: i18n.t('ats.settings.candidate_data.open_application_settings.form.gender'),
      fieldType: 'select',
      options: requirementOptions,
      helpText: i18n.t('ats.jobs_form.gender_information'),
    }),
  })
}

type OpenApplicationFormSchema = ReturnType<typeof buildSchema>

interface Props {
  openApplicationSetting: {
    id: string
    cvRequirement: AtsJobRequirementsOptionsEnum
    phoneRequirement: AtsJobRequirementsOptionsEnum
    photoRequirement: AtsJobRequirementsOptionsEnum
    personalUrlRequirement: AtsJobRequirementsOptionsEnum
    genderRequirement: AtsJobRequirementsOptionsEnum
  }
}

const OpenApplicationForm = ({ openApplicationSetting }: Props) => {
  const [update] = useMutation(UpdateOpenApplicationSettingDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsOpenApplicationSetting,
  })

  const { formRef } = useF0Form()
  const schema = useMemo(() => buildSchema(), [])

  const formDefinition = useF0FormDefinition<OpenApplicationFormSchema>({
    name: 'ats-open-application-settings',
    schema,
    defaultValues: {
      cvRequirement: openApplicationSetting.cvRequirement,
      phoneRequirement: openApplicationSetting.phoneRequirement,
      photoRequirement: openApplicationSetting.photoRequirement,
      personalUrlRequirement: openApplicationSetting.personalUrlRequirement,
      genderRequirement: openApplicationSetting.genderRequirement,
    },
    submitConfig: { type: 'default', label: i18n.t('common.submit') },
    errorTriggerMode: 'on-submit',
    onSubmit: async ({ data }): Promise<F0FormSubmitResult> => {
      const result = await update({
        variables: {
          id: openApplicationSetting.id,
          ...data,
        },
      })

      let fieldErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (handled.success) {
        return { success: true }
      }

      return { success: false, errors: fieldErrors }
    },
  })

  return <F0Form formDefinition={formDefinition} formRef={formRef} />
}

export default OpenApplicationForm
